import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { key: 2 }

import { useRouter } from 'vue-router';
import { watch, ref, onMounted, computed } from 'vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import { InsurancePolicy, ISitecoreFormGenericInput, VehicleCoverage } from '@/interfaces';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { getFormInput } from '@/utils/form-input-helper';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import { convertArrayToObject } from '@/utils/data-transforms';
import useFormInputData from '@/composables/useFormInputData';
import { SelfServiceService } from '@/services/store/self-service-service';
import { useAccountStore, useAnalyticsStore } from '@/store';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { FormEvents } from '@/enums/form-events.enum';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import { ChangeVehicleService } from '@/services/store/change-vehicle.service';
import { JP_GENERIC_CODE_SS } from '@/constants/jeopardy-codes.const';

enum ModalState {
  NEW_REGO = 'newRego',
  COMPLETE = 'complete',
  CMO_ERROR = 'cmoError',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MotorChangeRegistrationModal',
  props: {
    content: {},
    policy: {}
  },
  setup(__props: any) {

const props = __props;

const changeRegistrationTitle = getFormInput(props.content, 'changeRegistrationTitle');
const updateBtn = getFormInput(props.content, 'updateBtn');
const closeBtn = getFormInput(props.content, 'closeBtn');
const doneBtn = getFormInput(props.content, 'doneBtn');
const cmoError = getFormInput(props.content, 'cmoError');

const formInputs = convertArrayToObject(props.content);
const { formInputData } = useFormInputData(formInputs);

const router = useRouter();
const accountStore = useAccountStore();
const analyticsStore = useAnalyticsStore();

const currentModalState = ref<ModalState>(ModalState.NEW_REGO);
const showModal = ref(false);
const showValidationError = ref(false);
const isLoading = ref(false);
const newRegoDetails = ref<{
  registrationNumber?: string;
}>({});

const completedText = computed(() => {
  const richText = getFormInput(props.content, 'success')?.label?.value as string;
  return replaceBookmark('newRego', richText, newRegoDetails.value.registrationNumber ?? '');
});

watch(
  () => router.currentRoute.value.hash,
  (newVal) => {
    // Start change rego modal
    if (newVal.includes('#changeRego')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      router.currentRoute.value.hash = '';
      showModal.value = true;

      //-------------------------------
      // Push analytics START
      //-------------------------------
      analyticsStore.pushAnalytics({
        event: FormEvents.START,
        form: {
          name: 'Update vehicle registration',
          stepName: 'FormStarted',
          productName: (props.policy?.coverages?.at(0) as VehicleCoverage)?.vehicle?.formattedSummary,
        },
      });

      if (currentModalState.value != ModalState.NEW_REGO) {
        pushJeopardy('Invalid policy status');
      }
      //-------------------------------
      // Push analytics END
      //-------------------------------
    }
  },
  {
    deep: true,
  }
);

watch(showModal, (newVal, oldVal) => {
  if (!newVal && oldVal) resetModal();
});

const resetModal = () => {
  showModal.value = false;
  // Wait 1s for modal close animation to avoid users seeing flash
  setTimeout(() => {
    showValidationError.value = false;
    isLoading.value = false;
    newRegoDetails.value = {};
    if (!props.policy) return (currentModalState.value = ModalState.CMO_ERROR);
    currentModalState.value = ModalState.NEW_REGO;
  }, 1000);
};

onMounted(resetModal);

/**
 * Validate and update rego
 *
 * @param vehicleDetail
 */
const changeRego = async () => {
  try {
    showValidationError.value = false;
    isLoading.value = true;
    const policyNumber = router.currentRoute.value.query.policyNumber as string;
    const coverage = props.policy?.coverages?.[0] as VehicleCoverage | undefined;
    const vehicle = coverage?.vehicle;
    const newRego = newRegoDetails.value.registrationNumber?.toUpperCase().replaceAll(' ', '');

    if (!policyNumber || !coverage?.id || !vehicle || !newRego) {
      throw new Error(JP_GENERIC_CODE_SS);
    }

    newRegoDetails.value.registrationNumber = newRego;

    if (newRego === vehicle.registrationNumber?.toUpperCase()) {
      throw new Error('Same registration number');
    }

    // Create quote session
    const quoteResponse = await SelfServiceService.createQuoteSession(
      policyNumber,
      SelfServiceSessionPages.ChangeVehicleRegistrationPage,
      coverage.id
    );

    if (!quoteResponse?.success) {
      throw new Error(quoteResponse.errors?.[0]?.message ?? JP_GENERIC_CODE_SS);
    }

    // Change registration
    const sessionId = quoteResponse.data;
    const regoResponse = await ChangeVehicleService.changeVehicleRegistration(sessionId, newRego);

    if (!regoResponse?.success) {
      throw new Error(regoResponse.errors?.[0]?.message ?? JP_GENERIC_CODE_SS);
    }
    if (regoResponse.errors && regoResponse.errors.length > 0) {
      throw new Error(regoResponse.errors[0].message ?? JP_GENERIC_CODE_SS);
    }
    const jeopardies = regoResponse.data?.jeopardies;
    if (jeopardies && jeopardies.length > 0) {
      throw new Error(jeopardies[0].code);
    }

    // Analytics
    analyticsStore.pushAnalytics({
      event: FormEvents.COMPLETE,
      form: {
        name: 'Update vehicle registration',
        stepName: 'FormCompleted',
        productName: vehicle.formattedSummary,
      },
    });

    currentModalState.value = ModalState.COMPLETE;
    isLoading.value = false;
  } catch (e: any) {
    currentModalState.value = ModalState.CMO_ERROR;
    showValidationError.value = true;
    isLoading.value = false;
    pushJeopardy(e?.message ?? JP_GENERIC_CODE_SS);
    console.error(e);
  }
};

const pushJeopardy = (error: string) => {
  analyticsStore.pushAnalytics({
    event: FormEvents.JEOPARDY,
    form: {
      name: 'Update vehicle registration',
      formErrorType: error,
      productName: (props.policy?.coverages?.at(0) as VehicleCoverage)?.vehicle?.formattedSummary,
    },
  });
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createBlock(ModalDialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
    size: "md",
    showXCloseButton: true,
    closeOnEsc: false,
    isLoading: isLoading.value
  }, {
    default: _withCtx(() => [
      (ModalState.NEW_REGO == currentModalState.value)
        ? (_openBlock(), _createBlock(_component_FormKit, {
            key: 0,
            modelValue: newRegoDetails.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newRegoDetails).value = $event)),
            type: "form",
            onSubmit: changeRego
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(RichText), {
                field: _unref(changeRegistrationTitle)?.label
              }, null, 8, ["field"]),
              _createVNode(RACQFormKit, {
                bindings: _unref(formInputData)('registrationNumber')
              }, null, 8, ["bindings"]),
              _createVNode(_component_FormKit, {
                type: "submit",
                outerClass: showValidationError.value ? 'mt-8' : 'mt-2',
                inputClass: "btn-md btn w-full btn-primary",
                label: _unref(updateBtn)?.label?.value,
                disabled: isLoading.value
              }, null, 8, ["outerClass", "label", "disabled"])
            ]),
            _: 1
          }, 8, ["modelValue"]))
        : (ModalState.COMPLETE == currentModalState.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(_unref(RichText), {
                field: { value: completedText.value }
              }, null, 8, ["field"]),
              _createElementVNode("button", {
                class: "mt-2 btn w-full btn-md btn-primary",
                onClick: resetModal
              }, _toDisplayString(_unref(doneBtn)?.label?.value), 1)
            ]))
          : (ModalState.CMO_ERROR == currentModalState.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_unref(RichText), {
                  field: _unref(cmoError)?.label
                }, null, 8, ["field"]),
                _createElementVNode("button", {
                  class: "mt-2 btn w-full btn-md btn-primary",
                  onClick: resetModal
                }, _toDisplayString(_unref(closeBtn)?.label?.value), 1)
              ]))
            : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue", "isLoading"]))
}
}

})